<template>
    <div class="step-experience-selection">
        <div class="catalog-layout">
            <!-- MENU VERTICAL — remplace les anciens onglets du header ET les chips horizontales.
                 Trois niveaux : le TYPE (Expérience / Options / Resto), ses CATÉGORIES, et pour le
                 resto ses SOUS-CATÉGORIES.

                 Un type ne se déplie que lorsqu'il est actif : ses catégories ne sont chargées
                 qu'à ce moment-là (fetchChips / loadResto). Déplier un type inactif n'afficherait
                 donc qu'une liste vide — cliquer un type le sélectionne ET l'ouvre, et re-cliquer
                 le type courant le replie.

                 Les actions sont EXACTEMENT celles des anciennes chips : filtre serveur pour les
                 expériences et les produits, ancre de défilement pour le resto. -->
            <aside class="catalog-nav" :style="{ height: scrollAreaHeight + 'px' }">
                <!-- La recherche vit ICI et non dans le header : chercher et parcourir sont le
                     même geste — trouver un article — et tiennent donc dans la même colonne.
                     Elle porte sur le NOM et ignore la catégorie sélectionnée : on cherche
                     dans tout le catalogue du type courant, pas dans la branche ouverte. -->
                <div class="nav-search">
                    <Search :size="16" class="nav-search-icon" />
                    <input
                        v-model="search"
                        type="text"
                        class="nav-search-input poppinsRegular"
                        :placeholder="$t('common.search')"
                    />
                    <button v-if="search" type="button" class="nav-search-clear" @click="search = ''">
                        <X :size="14" />
                    </button>
                </div>

                <nav class="nav-tree">
                    <div v-for="type in typeTabs" :key="type.id" class="nav-group">
                        <button
                            type="button"
                            class="nav-type poppinsMedium"
                            :class="{ active: activeType === type.id }"
                            :aria-expanded="isTypeOpen(type.id) ? 'true' : 'false'"
                            @click="selectType(type.id)"
                        >
                            <ChevronRight :size="16" class="nav-caret" :class="{ open: isTypeOpen(type.id) }" />
                            <span class="nav-label">{{ type.label }}</span>
                            <!-- Combien de lignes sont déjà composées sous ce type. Permet de
                                 retrouver ses choix après avoir changé d'onglet. -->
                            <span v-if="composedCountFor(type.id) > 0" class="nav-badge">{{ composedCountFor(type.id) }}</span>
                        </button>

                        <ul v-if="isTypeOpen(type.id)" class="nav-children">
                            <!-- RESTO : le sommaire existant (« Menus » en tête puis les
                                 catégories), simplement décoré de son compteur. On ne le
                                 reconstruit pas depuis l'arbre : « Menus » n'est pas une
                                 catégorie de plats, et les ancres de défilement reposent sur
                                 ces sections. -->
                            <template v-if="type.id === 'resto'">
                                <li v-if="restoLoading" class="nav-empty poppinsLight">
                                    {{ $t('devis.v3.nav_loading') }}
                                </li>
                                <li v-for="section in restoSections" :key="section.key" class="nav-child-wrap">
                                    <div class="nav-child-row">
                                        <button
                                            type="button"
                                            class="nav-child"
                                            :class="{ active: activeRestoKey === section.key }"
                                            @click="goToRestoSection(section.key)"
                                        >
                                            {{ section.name }}
                                        </button>
                                        <span v-if="composedRestoSection(section) > 0" class="nav-badge">{{
                                            composedRestoSection(section)
                                        }}</span>
                                        <span v-if="restoCount(section) !== null" class="nav-count">{{
                                            restoCount(section)
                                        }}</span>
                                        <button
                                            v-if="section.subcategories && section.subcategories.length > 0"
                                            type="button"
                                            class="nav-toggle"
                                            :aria-expanded="isSectionOpen(section.key) ? 'true' : 'false'"
                                            @click.stop="toggleSection(section.key)"
                                        >
                                            <ChevronRight
                                                :size="14"
                                                class="nav-caret"
                                                :class="{ open: isSectionOpen(section.key) }"
                                            />
                                        </button>
                                    </div>

                                    <ul v-if="isSectionOpen(section.key)" class="nav-grandchildren">
                                        <li
                                            v-for="category in section.subcategories"
                                            :key="section.key + '-' + category.slug"
                                            class="nav-child-row"
                                        >
                                            <button
                                                type="button"
                                                class="nav-grandchild"
                                                :class="{ active: isSubcategoryActive(section, category) }"
                                                @click="selectRestoSubcategory(section, category)"
                                            >
                                                {{ category.name }}
                                            </button>
                                            <span v-if="composedForSlug(category.slug) > 0" class="nav-badge">{{
                                                composedForSlug(category.slug)
                                            }}</span>
                                            <span v-if="slugCount(category.slug) !== null" class="nav-count">{{
                                                slugCount(category.slug)
                                            }}</span>
                                        </li>
                                    </ul>
                                </li>
                            </template>

                            <!-- EXPÉRIENCES et PRODUITS : l'arbre renvoyé par /items/tree, aplati
                                 pour se rendre à profondeur quelconque sans composant récursif.
                                 Chaque nœud porte son compteur (descendants inclus) et son chemin
                                 complet, exposé en infobulle. -->
                            <template v-else>
                                <li v-if="treeLoading" class="nav-empty poppinsLight">
                                    {{ $t('devis.v3.nav_loading') }}
                                </li>
                                <li class="nav-child-row">
                                    <button
                                        type="button"
                                        class="nav-child"
                                        :class="{ active: activeNodeKey === null }"
                                        @click="selectNode(null)"
                                    >
                                        {{ $t('devis.v3.all_filter') }}
                                    </button>
                                </li>
                                <li v-for="row in visibleNodes" :key="row.key" class="nav-child-row">
                                    <button
                                        type="button"
                                        class="nav-child"
                                        :class="{ active: activeNodeKey === row.key }"
                                        :style="{ paddingLeft: 10 + row.depth * 12 + 'px' }"
                                        :title="row.node.path"
                                        @click="selectNode(row.node)"
                                    >
                                        {{ row.node.label }}
                                    </button>
                                    <span v-if="composedForNode(row.node) > 0" class="nav-badge">{{
                                        composedForNode(row.node)
                                    }}</span>
                                    <span class="nav-count">{{ row.node.count }}</span>
                                    <button
                                        v-if="row.node.children && row.node.children.length > 0"
                                        type="button"
                                        class="nav-toggle"
                                        :aria-expanded="isNodeOpen(row.key) ? 'true' : 'false'"
                                        @click.stop="toggleNode(row.key)"
                                    >
                                        <ChevronRight :size="14" class="nav-caret" :class="{ open: isNodeOpen(row.key) }" />
                                    </button>
                                </li>
                            </template>
                        </ul>
                    </div>
                </nav>
            </aside>

            <div class="catalog-content">
                <!-- RESTO : rendu dédié. Les catégories servent de sommaire (ancres) et toutes les
                     sections sont empilées dans la zone défilante, « Menus » en tête. Le reste du
                     composant — expériences, formules, options — n'est pas concerné. -->
                <template v-if="isResto">
                    <div class="scroll-area-v3" ref="restoScroll" :style="{ height: scrollAreaHeight + 'px' }">
                        <div v-if="restoLoading" class="item-list-v3">
                            <div v-for="n in 8" :key="'sk-' + n" class="skeleton-row-v3"></div>
                        </div>

                        <template v-else>
                            <div
                                v-for="section in displayedRestoSections"
                                :key="section.key"
                                :ref="'resto-' + section.key"
                                class="resto-section"
                            >
                                <!-- Titre de section, qui devient un fil d'Ariane dès qu'on est
                                     descendu dans une sous-catégorie : chaque niveau reste cliquable
                                     pour remonter, la section voisine n'étant pas concernée. -->
                                <h4 class="resto-section-title poppinsSemiBold ppv2-text-base ppv2-text-black">
                                    <button
                                        v-if="section.path.length > 0"
                                        type="button"
                                        class="resto-crumb"
                                        @click="goToSectionLevel(section, -1)"
                                    >
                                        {{ section.name }}
                                    </button>
                                    <span v-else>{{ section.name }}</span>

                                    <template v-for="(node, index) in section.path">
                                        <ChevronRight
                                            :key="'sep-' + section.key + '-' + node.slug"
                                            :size="14"
                                            class="resto-crumb-sep"
                                        />
                                        <button
                                            :key="section.key + '-' + node.slug"
                                            type="button"
                                            class="resto-crumb"
                                            :class="{ 'is-current': index === section.path.length - 1 }"
                                            @click="goToSectionLevel(section, index)"
                                        >
                                            {{ node.name }}
                                        </button>
                                    </template>
                                </h4>

                                <div v-if="section.loading" class="item-list-v3">
                                    <div v-for="n in 4" :key="section.key + '-sk-' + n" class="skeleton-row-v3"></div>
                                </div>

                                <div v-else-if="section.items.length > 0" class="item-list-v3">
                                    <template v-for="food in section.items">
                                        <div
                                            :key="foodKey(section, food)"
                                            class="item-row-v3"
                                            :class="{
                                                selected: !isCategoryCard(food) && basketQty(food) > 0,
                                                'is-category': isCategoryCard(food),
                                                'is-open': isFoodConfigOpen(section, food),
                                            }"
                                            @click="selectRestoItem(section, food)"
                                        >
                                            <div class="row-thumb">
                                                <img v-if="food.image" :src="food.image" :alt="food.name" />
                                                <img v-else src="/media/placeholder_devis_items.jpeg" alt="placeholder" />
                                            </div>

                                            <div class="row-main">
                                                <h3 class="row-title poppinsSemiBold">{{ food.name }}</h3>
                                                <div class="row-meta">
                                                    <!-- Une sous-catégorie n'a ni prix ni quantité :
                                                         elle ouvre son contenu. -->
                                                    <span v-if="isCategoryCard(food)" class="row-pill">
                                                        {{ $t('devis.v3.food_open_category') }}
                                                    </span>
                                                    <!-- Plat à configurer : le badge remplace la
                                                         popup d'antan — le détail se déplie sous la
                                                         ligne. Une fois des choix faits, il les
                                                         résume au lieu d'inviter à configurer. -->
                                                    <span v-else-if="foodNeedsConfig(food)" class="row-pill row-pill--config">
                                                        <Settings2 :size="12" />
                                                        {{ foodConfigLabel(food) }}
                                                    </span>
                                                </div>
                                            </div>

                                            <template v-if="!isCategoryCard(food)">
                                                <div class="row-price poppinsSemiBold">{{ formatFoodPrice(food) }}</div>

                                                <!-- Un plat à configurer n'a pas de « + » direct :
                                                     chaque exemplaire peut être configuré
                                                     différemment, il passe par le panneau. -->
                                                <div v-if="basketQty(food) > 0" class="row-qty" @click.stop>
                                                    <button type="button" @click.stop="decrementBasket(food)">
                                                        <Minus :size="14" />
                                                    </button>
                                                    <span class="qty-value poppinsSemiBold">{{ basketQty(food) }}</span>
                                                    <button type="button" @click.stop="incrementFood(food)">
                                                        <Plus :size="14" />
                                                    </button>
                                                </div>
                                                <div v-else class="row-qty-placeholder"></div>

                                                <div class="row-total poppinsSemiBold">
                                                    <template v-if="basketQty(food) > 0">{{
                                                        formatPrice(foodTotal(food))
                                                    }}</template>
                                                </div>

                                                <button
                                                    v-if="basketQty(food) > 0"
                                                    type="button"
                                                    class="row-remove"
                                                    :aria-label="$t('devis.v3.remove_line')"
                                                    @click.stop="removeFood(food)"
                                                >
                                                    <X :size="15" />
                                                </button>
                                            </template>

                                            <ChevronRight v-else :size="18" class="row-chevron" />
                                        </div>

                                        <!-- Panneau de configuration DÉPLIÉ sous le plat : mêmes
                                             suppléments, mêmes options et même validation qu'avant,
                                             mais sans popup par-dessus la popup. -->
                                        <div
                                            v-if="isFoodConfigOpen(section, food)"
                                            :key="foodKey(section, food) + '-config'"
                                            class="row-config"
                                            @click.stop
                                        >
                                            <FoodDetailPanel
                                                :food="food"
                                                :initial-state="foodConfig(food)"
                                                inline
                                                @change="handleFoodConfigChange"
                                                @close="closeFoodConfig"
                                            />
                                        </div>
                                    </template>
                                </div>

                                <p v-else class="resto-section-empty poppinsLight ppv2-text-sm ppv2-text-black ppv2-opacity-60">
                                    {{ $t('devis.v3.no_food_in_category') }}
                                </p>
                            </div>

                            <div v-if="displayedRestoSections.length === 0" class="empty-state-v3">
                                <p>{{ $t('devis.v3.no_food_available') }}</p>
                            </div>
                        </template>
                    </div>
                </template>

                <template v-else>
                    <!-- Le filtre par TAG (expériences) ou par SOUS-CATÉGORIE boutique (produits) est
                         passé dans le menu de gauche. Le filtrage reste résolu côté SERVEUR : la
                         pagination empêche tout filtrage client fiable. -->
                    <div
                        class="scroll-area-v3"
                        ref="scrollContainer"
                        @scroll="handleScroll"
                        :style="{ height: scrollAreaHeight + 'px' }"
                    >
                        <!-- LISTE verticale : une ligne par article. Vignette, nom, méta
                             (durée ou rattachement), prix unitaire, puis — pour ce qui se met au
                             panier — le sélecteur de quantité, le total de la ligne et le retrait.
                             Une EXPÉRIENCE n'a ni quantité ni total ici : elle reste une sélection
                             unique qui ouvre les étapes Participants et Créneau. -->
                        <div class="item-list-v3" v-if="items.length > 0">
                            <template v-for="item in items">
                                <div
                                    :key="item.id"
                                    class="item-row-v3"
                                    :class="{
                                        selected: isCardSelected(item) || isStaged(item),
                                        'is-open': !isProduct && selectedId === item.id,
                                    }"
                                    @click="onCardClick(item)"
                                >
                                    <div class="row-thumb">
                                        <img v-if="item.image" :src="item.image" :alt="item.name" />
                                        <img v-else src="/media/placeholder_devis_items.jpeg" alt="placeholder" />
                                    </div>

                                    <div class="row-main">
                                        <!-- Libellé éditable : le survol révèle le crayon, le clic
                                             ouvre le champ. Le nom d'origine sert d'amorce ; ne rien
                                             changer n'envoie aucun libellé personnalisé. -->
                                        <div v-if="labelEditingId === item.id" class="row-title-edit" @click.stop>
                                            <input
                                                ref="labelInput"
                                                class="row-title-input poppinsSemiBold"
                                                :value="itemLabel"
                                                @input="$emit('update:itemLabel', $event.target.value)"
                                                @keyup.enter="labelEditingId = null"
                                                @blur="labelEditingId = null"
                                            />
                                        </div>
                                        <h3 v-else class="row-title poppinsSemiBold">
                                            <span>{{ rowLabel(item) }}</span>
                                            <button
                                                type="button"
                                                class="row-inline-action"
                                                :aria-label="$t('devis.v3.rename_line')"
                                                @click.stop="startLabelEdit(item)"
                                            >
                                                <Pencil :size="13" />
                                            </button>
                                        </h3>
                                        <!-- Article SÉLECTIONNÉ : les pills laissent la place aux
                                             réglages, posés SUR la ligne — formule, nombre de
                                             personnes et options propres au type. Ni étape
                                             intermédiaire, ni panneau à déplier. -->
                                        <div v-if="showsParticipants(item)" class="row-participants">
                                            <div v-if="isLoadingDetails || !itemDetails" class="row-config-loading">
                                                <div class="spinner-v3-small"></div>
                                            </div>
                                            <step-participants
                                                v-else
                                                compact
                                                :value="pax"
                                                :item-details="itemDetails"
                                                :mixing="paxMixing"
                                                :duration-id="durationId"
                                                :ticket-selections="ticketSelections"
                                                :uses-time-slots="usesTimeSlots"
                                                :lock-space="lockSpace"
                                                :initialised="participantsInitialised"
                                                @input="$emit('update:pax', $event)"
                                                @update:mixing="$emit('update:paxMixing', $event)"
                                                @update:durationId="$emit('update:durationId', $event)"
                                                @update:ticketSelections="$emit('update:ticketSelections', $event)"
                                                @update:lockSpace="$emit('update:lockSpace', $event)"
                                                @update:initialised="$emit('update:participantsInitialised', $event)"
                                            />
                                        </div>

                                        <!-- Note de ligne, saisissable DÈS l'ajout : le même champ
                                             que sur le devis (InlineNoteField), plus besoin de
                                             rouvrir la ligne après coup pour l'écrire. -->
                                        <div v-if="noteEditingId === item.id" class="row-note-edit" @click.stop>
                                            <InlineNoteField
                                                :value="itemNote"
                                                :placeholder="$t('devis.v3.add_note')"
                                                @input="$emit('update:itemNote', $event)"
                                            />
                                        </div>

                                        <div v-else class="row-meta">
                                            <!-- Une expérience se repère à sa DURÉE, un produit à son
                                             rattachement : ni l'un ni l'autre n'a les deux. -->
                                            <span v-if="item.duration_text" class="row-pill">
                                                <Clock :size="12" />
                                                {{ item.duration_text }}
                                            </span>
                                            <span v-else-if="item.badge" class="row-pill">{{ item.badge }}</span>
                                            <button
                                                type="button"
                                                class="row-note-btn"
                                                :class="{ 'has-note': rowNote(item) }"
                                                :aria-label="$t('devis.v3.add_note')"
                                                @click.stop="startNoteEdit(item)"
                                            >
                                                <StickyNote :size="12" />
                                                <span>{{ rowNote(item) ? '+ Note' : $t('devis.v3.add_note') }}</span>
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Choix de la date : même geste que les autres actions de
                                         ligne — une icône au survol. Elle remplace le bouton
                                         « Suivant » du pied : dater se fait sur la ligne
                                         concernée, pas au bout d'un parcours. -->
                                    <button
                                        v-if="!isProduct"
                                        type="button"
                                        class="row-schedule-btn"
                                        :class="{ 'has-value': showsParticipants(item) && scheduleLabel }"
                                        :aria-label="$t('devis.v3.choose_date')"
                                        @click.stop="openPlanning(item)"
                                    >
                                        <CalendarDays :size="14" />
                                        <span v-if="showsParticipants(item) && scheduleLabel">{{ scheduleLabel }}</span>
                                    </button>

                                    <!-- Prix UNITAIRE TTC : celui que l'étape Date affichera sur
                                         le créneau, puisqu'il sort de la même fonction serveur —
                                         il suit donc la formule et le nombre de participants.
                                         Négociable dès l'ajout : le survol révèle le crayon —
                                         sauf sur un billet groupé, dont la ligne principale est à
                                         0 € et dont le montant vit sur ses billets. -->
                                    <div
                                        v-if="priceEditingId === item.id && !isTicketContainerRow(item)"
                                        class="row-price-edit"
                                        @click.stop
                                    >
                                        <input
                                            ref="priceInput"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            class="row-price-input poppinsSemiBold"
                                            :value="rowUnitPrice(item)"
                                            @input="onPriceInput($event)"
                                            @keyup.enter="priceEditingId = null"
                                            @blur="priceEditingId = null"
                                        />
                                    </div>
                                    <div v-else class="row-price poppinsSemiBold" :class="{ negotiated: isNegotiated(item) }">
                                        <span>{{ formatPrice(rowUnitPrice(item)) }}</span>
                                        <button
                                            v-if="!isProduct && !isTicketContainerRow(item)"
                                            type="button"
                                            class="row-inline-action"
                                            :aria-label="$t('devis.v3.edit_price')"
                                            @click.stop="startPriceEdit(item)"
                                        >
                                            <Pencil :size="13" />
                                        </button>
                                    </div>

                                    <!-- `.stop` sur le bloc entier : sans lui chaque clic sur - ou +
                                     remonterait à la ligne et rejouerait un ajout. -->
                                    <div v-if="isProduct" class="row-qty" @click.stop>
                                        <button
                                            type="button"
                                            :disabled="productQty(item) === 0"
                                            @click.stop="decrementProduct(item)"
                                        >
                                            <Minus :size="14" />
                                        </button>
                                        <span class="qty-value poppinsSemiBold">{{ productQty(item) }}</span>
                                        <button type="button" @click.stop="addProductToBasket(item)">
                                            <Plus :size="14" />
                                        </button>
                                    </div>

                                    <div v-if="isProduct" class="row-total poppinsSemiBold">
                                        <template v-if="productQty(item) > 0">{{
                                            formatPrice(item.price * productQty(item))
                                        }}</template>
                                    </div>

                                    <!-- Total de la ligne sélectionnée : prix unitaire × quantité
                                         retenue par le serveur (un billet par participant, ou une
                                         seule ligne si l'espace est privatisé). -->
                                    <div v-else class="row-total poppinsSemiBold">
                                        <template v-if="rowLineTotal(item) !== null">{{
                                            formatPrice(rowLineTotal(item))
                                        }}</template>
                                    </div>

                                    <button
                                        v-if="isProduct && productQty(item) > 0"
                                        type="button"
                                        class="row-remove"
                                        :aria-label="$t('devis.v3.remove_line')"
                                        @click.stop="removeProduct(item)"
                                    >
                                        <X :size="15" />
                                    </button>

                                    <div v-if="!isProduct && (selectedId === item.id || isStaged(item))" class="row-check-cell">
                                        <div class="row-check">
                                            <Check :size="15" />
                                        </div>
                                        <button
                                            type="button"
                                            class="row-remove row-remove--swap"
                                            :aria-label="$t('devis.v3.remove_line')"
                                            :title="$t('devis.v3.remove_line')"
                                            @click.stop="$emit('remove-experience', item)"
                                        >
                                            <X :size="15" />
                                        </button>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <!-- Skeleton Loading -->
                        <div v-if="loading && items.length === 0" class="item-list-v3">
                            <div v-for="n in 8" :key="n" class="skeleton-row-v3"></div>
                        </div>

                        <!-- Empty State -->
                        <div v-if="!loading && items.length === 0" class="empty-state-v3">
                            <p v-if="searchQuery || activeNodeKey !== null">{{ $t('devis.v3.no_item_found') }}</p>
                            <p v-else>{{ $t('devis.v3.no_item_available') }}</p>
                        </div>

                        <!-- Loading More Spinner -->
                        <div v-if="loadingMore" class="loading-more-container">
                            <div class="spinner-v3-small"></div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { Search, Plus, Minus, ChevronRight, X, Clock, Check, Settings2, Pencil, StickyNote, CalendarDays } from 'lucide-vue'
import FoodDetailPanel from './FoodDetailPanel.vue'
import StepParticipants from './StepParticipants.vue'
import InlineNoteField from './InlineNoteField.vue'
import { buildFoodQuotePayload } from '../foodQuotePayload'

export default {
    name: 'StepItemSelection',
    components: {
        Search,
        X,
        Clock,
        Check,
        Settings2,
        Pencil,
        StickyNote,
        CalendarDays,
        Plus,
        Minus,
        ChevronRight,
        FoodDetailPanel,
        StepParticipants,
        InlineNoteField,
    },
    props: {
        activeType: { type: String, required: true },
        // Premier niveau du menu de gauche : les mêmes entrées que les anciens onglets du
        // header, libellés compris. Le parent reste seul maître du type actif — il porte
        // aussi la remise à zéro de la sélection (QuoteItemStepper::selectTypeTab).
        typeTabs: { type: Array, default: () => [] },
        selectedId: { type: [Number, String], default: null },
        searchQuery: { type: String, default: '' },
        // Réglage des participants, déplié sous l'article sélectionné. L'ÉTAT reste chez le
        // stepper — il le transmet aux étapes suivantes et le remet à zéro d'un article à
        // l'autre ; ce composant ne fait que l'afficher et le remonter.
        itemDetails: { type: Object, default: null },
        isLoadingDetails: { type: Boolean, default: false },
        // Prix de la ligne pour la formule et le nombre de participants courants, calculé par le
        // serveur (même fonction que les créneaux). `null` = pas encore connu : la ligne retombe
        // alors sur le prix catalogue plutôt que d'annoncer un montant faux.
        livePrice: { type: Object, default: null },
        // Note libre et libellé personnalisé de la ligne en cours d'ajout. L'état vit chez le
        // stepper : il part avec la réservation, comme la formule et les participants.
        itemNote: { type: String, default: '' },
        itemLabel: { type: String, default: '' },
        // Prix TTC négocié sur la ligne. `null` = celui calculé par le serveur.
        itemPrice: { type: Number, default: null },
        // Résumé de la date retenue pour la ligne en cours ('Mer. 26 août · 14:30',
        // 'À planifier plus tard'…). Vide tant qu'aucune décision n'est prise.
        scheduleLabel: { type: String, default: '' },
        // Articles déjà composés et mis de côté : ils restent marqués comme retenus, même
        // quand la composition en cours porte sur un autre.
        stagedIds: { type: Array, default: () => [] },
        // Nombre de lignes déjà composées par type ({ experience: 3, option: 0, resto: 2 }).
        // Sert au repère du menu, pas au filtrage.
        composedByType: { type: Object, default: () => ({}) },
        // Lignes composées par nœud du menu, clé « param:value ». Les catégories d'expériences
        // s'en servent pour indiquer sous laquelle les choix ont été faits.
        composedByNode: { type: Object, default: () => ({}) },
        usesTimeSlots: { type: Boolean, default: true },
        pax: { type: Number, default: null },
        paxMixing: { type: Boolean, default: false },
        durationId: { type: [Number, String], default: null },
        ticketSelections: { type: Object, default: () => ({}) },
        lockSpace: { type: Boolean, default: false },
        participantsInitialised: { type: Boolean, default: false },
    },
    data() {
        return {
            items: [],
            // Arbre du rail pour le type courant (expériences / produits), tel que renvoyé par
            // /items/tree : chaque nœud porte label, path, count (descendants inclus) et le
            // couple param/value qui dit comment filtrer dessus.
            tree: [],
            treeLoading: false,
            // Nœuds dépliés, et nœud sélectionné, désignés par leur clé « param:value » — les
            // identifiants d'un tag, d'une catégorie et d'une sous-catégorie viennent de trois
            // tables et peuvent se recouvrir.
            expandedNodes: [],
            activeNode: null,
            // Types repliés à la main. Un type n'est dépliable que s'il est actif (ses
            // catégories ne sont chargées qu'à ce moment-là) : on mémorise donc le REPLI
            // volontaire, pas l'ouverture — par défaut le type actif est ouvert.
            collapsedTypes: [],
            // Sections resto dépliées sur leurs sous-catégories.
            openSections: [],
            // Compteurs resto indexés par slug de catégorie, issus du même /items/tree.
            // Le sommaire resto reste construit par loadResto ; l'arbre ne fait que le décorer.
            restoCounts: {},
            // Résultats d'une recherche resto, tenus À PART de restoSections pour que le rail
            // ne bouge pas pendant la recherche : seule la grille change.
            restoSearchItems: [],
            // Panier produits, indexé par id : { id, name, price, image, quantity }. Un produit
            // n'a ni option ni supplément — pas de `lines` comme au resto, une entrée suffit.
            productBasket: {},
            loading: false,
            loadingMore: false,
            page: 1,
            hasMore: true,
            searchTimeout: null,
            scrollAreaHeight: 400,
            // RESTO — sections empilées : « Menus » (packs) puis une section par catégorie.
            restoSections: [],
            restoLoading: false,
            activeRestoKey: null,
            // Panier resto, indexé par uuid : { uuid, name, price, quantity, lines[] }.
            // `lines` ne sert qu'aux plats configurés (suppléments/options) — un même plat
            // peut y figurer plusieurs fois avec des configurations différentes.
            restoBasket: {},
            // Configuration retenue par plat, indexée par clé de panier. Elle survit à la
            // fermeture du panneau ET au retrait du panier : rouvrir un plat doit le retrouver
            // tel qu'on l'avait laissé.
            foodConfigs: {},
            // Ligne dont le libellé ou la note est en cours de saisie. Une seule à la fois :
            // deux champs ouverts dans la même liste rendraient la colonne illisible.
            labelEditingId: null,
            noteEditingId: null,
            priceEditingId: null,
            // Plat dont le panneau de configuration est déplié, désigné par sa clé de ligne
            // (`foodKey`). Une seule configuration ouverte à la fois : deux panneaux dépliés
            // dans la même liste rendraient la colonne illisible.
            openFoodConfigKey: null,
        }
    },
    computed: {
        isResto() {
            return this.activeType === 'resto'
        },
        // Onglet « Options » = produits boutique. Il se comporte comme le resto (panier +
        // compteur) et non comme les expériences (sélection unique puis étapes suivantes).
        isProduct() {
            return this.activeType === 'option'
        },
        /**
         * La saisie vit ici, l'état reste chez le parent (prop `searchQuery` synchronisée) :
         * il la transmet aussi aux autres étapes et la remet à zéro à l'ouverture du stepper.
         */
        search: {
            get() {
                return this.searchQuery
            },
            set(value) {
                this.$emit('update:searchQuery', value)
            },
        },
        activeNodeKey() {
            return this.activeNode ? this.nodeKey(this.activeNode) : null
        },
        restoSearchActive() {
            return this.isResto && (this.searchQuery || '').trim() !== ''
        },
        /**
         * Ce que la GRILLE resto affiche : les résultats de recherche s'ils existent, sinon le
         * sommaire habituel. `restoSections` — donc le rail — n'est jamais remplacé.
         */
        displayedRestoSections() {
            if (!this.restoSearchActive) {
                return this.restoSections
            }

            return [
                {
                    key: 'search',
                    name: this.$t('devis.v3.search_results'),
                    slug: null,
                    items: this.restoSearchItems,
                    path: [],
                    loading: false,
                    subcategories: [],
                },
            ]
        },
        /**
         * L'arbre aplati en lignes { key, node, depth }, en ne descendant que dans les nœuds
         * ouverts.
         *
         * Aplatir plutôt qu'écrire un composant récursif : la profondeur est libre (les tags
         * s'imbriquent autant qu'on veut) et une liste plate se rend, se stylise et se lit sans
         * indirection supplémentaire. La borne de profondeur protège d'un cycle en base, comme
         * côté serveur.
         */
        visibleNodes() {
            const rows = []

            const walk = (nodes, depth) => {
                if (depth >= 10) return

                nodes.forEach((node) => {
                    const key = this.nodeKey(node)
                    rows.push({ key, node, depth })

                    if (node.children && node.children.length > 0 && this.isNodeOpen(key)) {
                        walk(node.children, depth + 1)
                    }
                })
            }

            walk(this.tree, 0)

            return rows
        },
    },
    watch: {
        searchQuery: {
            handler(value) {
                if (this.searchTimeout) clearTimeout(this.searchTimeout)

                // Effacement (typiquement un clic sur un nœud du rail) : rien à temporiser,
                // on recharge immédiatement. Seule la frappe est debouncée.
                if (!value) {
                    this.applySearch()
                    return
                }

                this.searchTimeout = setTimeout(() => this.applySearch(), 500)
            },
        },
        activeType: {
            immediate: false,
            handler() {
                // Changement d'onglet : on repart d'un rail neutre, aucun nœud sélectionné.
                this.activeNode = null
                this.expandedNodes = []
                this.tree = []

                // Le resto a son propre chargement : ni arbre paginé, ni pagination catalogue.
                if (this.isResto) {
                    this.loadResto()
                    this.$nextTick(() => {
                        this.updateScrollAreaHeight()
                    })
                    return
                }

                this.fetchTree()
                this.resetAndLoad()
                this.$nextTick(() => {
                    this.updateScrollAreaHeight()
                })
            },
        },
    },
    mounted() {
        if (this.isResto) {
            this.loadResto()
            this.$nextTick(() => {
                this.updateScrollAreaHeight()
            })
            return
        }

        this.fetchTree()
        this.resetAndLoad()
        this.$nextTick(() => {
            this.updateScrollAreaHeight()
        })
    },
    methods: {
        updateScrollAreaHeight() {
            // Cap at 450px so 12 cards (3 rows) always overflow and the scrollbar is visible.
            // Plus de déduction pour la barre de chips : les catégories sont passées dans le
            // menu de gauche, qui occupe la même hauteur que la zone défilante au lieu de
            // s'empiler au-dessus.
            this.scrollAreaHeight = Math.min(450, Math.floor(window.innerHeight * 0.9) - 284)
        },
        /* ---------------------------------------------------------------- *
         *  Menu vertical (types → catégories → sous-catégories)
         *  Aucune de ces méthodes ne change ce qui est chargé ni filtré :
         *  elles n'orchestrent que l'ouverture des niveaux et rappellent les
         *  actions existantes (selectChip, goToRestoSection, loadSectionLevel).
         * ---------------------------------------------------------------- */
        isTypeOpen(typeId) {
            return this.activeType === typeId && !this.collapsedTypes.includes(typeId)
        },
        /**
         * Clic sur un type : bascule dessus s'il n'est pas actif (et l'ouvre), sinon replie
         * ou déplie celui qui l'est déjà.
         *
         * Le changement de type est REMONTÉ au parent : c'est lui qui porte `activeType` et
         * qui remet à zéro la sélection en cours. Le watcher de `activeType` déclenche ensuite
         * le chargement des catégories, comme du temps des onglets du header.
         */
        selectType(typeId) {
            if (this.activeType === typeId) {
                this.collapsedTypes = this.collapsedTypes.includes(typeId)
                    ? this.collapsedTypes.filter((id) => id !== typeId)
                    : [...this.collapsedTypes, typeId]
                return
            }

            this.collapsedTypes = this.collapsedTypes.filter((id) => id !== typeId)
            this.$emit('type-change', typeId)
        },
        isSectionOpen(key) {
            return this.openSections.includes(key)
        },
        toggleSection(key) {
            this.openSections = this.isSectionOpen(key) ? this.openSections.filter((k) => k !== key) : [...this.openSections, key]
        },
        /**
         * Une sous-catégorie du menu ouvre le même niveau que sa carte dans la grille, à ceci
         * près qu'elle repart TOUJOURS de la tête de section : `[category]` et non
         * `[...section.path, category]`. Cliquer « Softs » depuis le menu doit ouvrir les
         * softs, que la section montre déjà un niveau plus profond ou non.
         */
        selectRestoSubcategory(section, category) {
            this.goToRestoSection(section.key)
            this.loadSectionLevel(section, [{ slug: category.slug, name: category.name }])
        },
        isSubcategoryActive(section, category) {
            return section.path.length > 0 && section.path[0].slug === category.slug
        },
        /**
         * Charge le sommaire resto : « Menus » (packs) en tête, puis une section par catégorie
         * avec ses plats. Les plats de chaque catégorie sont récupérés en parallèle — une
         * requête par catégorie, l'API ne sachant pas les renvoyer groupés.
         */
        async loadResto() {
            this.restoLoading = true
            this.restoSections = []
            this.openSections = []
            this.fetchRestoCounts()
            // Le panier n'est PAS vidé ici : recharger le menu (changement d'onglet, retour
            // à l'étape 1) ne doit pas effacer ce que l'utilisateur a déjà composé.

            try {
                const [packsRes, categoriesRes] = await Promise.all([
                    axios.get('/api/v3/food/packs'),
                    axios.get('/api/v3/food/categories', { params: { active: true, table: null } }),
                ])

                const sections = [
                    {
                        key: 'menus',
                        name: this.$t('devis.v3.food_menus'),
                        slug: null,
                        // `type: 'pack'` marque la carte à la source : le clic et la popup de
                        // détail savent alors quel endpoint interroger, sans avoir à déduire
                        // la nature de l'article de la section où il se trouve.
                        items: (packsRes.data?.data || []).map((pack) => ({ ...pack, type: 'pack' })),
                        path: [],
                        loading: false,
                        // Les menus ne rangent pas de sous-catégories : le niveau 3 du menu de
                        // gauche n'a rien à proposer ici, et l'entrée reste sans chevron.
                        subcategories: [],
                    },
                ]

                const categories = categoriesRes.data?.data || []
                const foodsByCategory = await Promise.all(categories.map((category) => this.fetchCategoryItems(category.slug)))

                categories.forEach((category, index) => {
                    const items = foodsByCategory[index] || []

                    sections.push({
                        key: category.slug || category.uuid,
                        name: category.name,
                        slug: category.slug,
                        items,
                        // Descente à l'intérieur de CETTE section ([{slug, name}]) : vide tant
                        // qu'on est sur la catégorie de tête. Les autres sections ne bougent pas.
                        path: [],
                        loading: false,
                        // Sous-catégories de TÊTE, figées au chargement : `items` est remplacé
                        // par le contenu du niveau ouvert dès qu'on descend, il ne peut donc
                        // pas servir de source au menu — celui-ci disparaîtrait au premier clic.
                        // Aucun appel supplémentaire : elles arrivent déjà avec les plats.
                        subcategories: items.filter((item) => this.isCategoryCard(item)),
                    })
                })

                this.restoSections = sections
                this.activeRestoKey = sections.length > 0 ? sections[0].key : null
            } catch (error) {
                console.error('Chargement du menu resto impossible :', error)
                this.restoSections = []
            } finally {
                this.restoLoading = false
                this.$nextTick(() => this.updateScrollAreaHeight())
            }
        },
        /**
         * Contenu d'une catégorie : ses plats ET ses sous-catégories.
         *
         * L'endpoint renvoie les deux dans la même liste, les sous-catégories portant
         * `type: 'category'`. Elles étaient jetées ici : une catégorie qui ne range que des
         * sous-catégories s'affichait alors vide, alors qu'elle a bien quelque chose à montrer.
         * Seules les sous-catégories INACTIVES sont écartées — une carte qui n'ouvre rien ne
         * doit pas être proposée (`is_active_for_current_pos` vaut `active` hors POS).
         * Les plats, eux, restent filtrés côté serveur comme avant.
         */
        fetchCategoryItems(slug) {
            return (
                axios
                    .get('/api/v3/food/', {
                        params: {
                            categories: slug,
                            page: 1,
                            pagination: 200,
                            all: true,
                            table: null,
                        },
                    })
                    .then((res) =>
                        (res.data?.data || []).filter(
                            (item) => item.type !== 'category' || item.is_active_for_current_pos !== false
                        )
                    )
                    // Une catégorie en erreur ne doit pas vider tout le sommaire.
                    .catch(() => [])
            )
        },
        isCategoryCard(item) {
            return item?.type === 'category'
        },
        /**
         * Descend d'un niveau DANS la section : ses cartes sont remplacées par le contenu de
         * la sous-catégorie ouverte, et le titre de section devient le chemin parcouru. Le
         * reste du sommaire est intact — on ne quitte pas la page de la carte.
         */
        openSectionCategory(section, category) {
            this.loadSectionLevel(section, [...section.path, { slug: category.slug, name: category.name }])
        },
        /**
         * Charge le niveau désigné par `path` (vide = catégorie de tête de la section).
         * `path` n'est appliqué qu'après la réponse : sur erreur, la section reste sur le
         * niveau qu'elle affichait plutôt que d'annoncer un chemin qu'elle ne montre pas.
         */
        async loadSectionLevel(section, path) {
            const slug = path.length > 0 ? path[path.length - 1].slug : section.slug
            if (!slug) return

            section.loading = true

            const items = await this.fetchCategoryItems(slug)

            section.items = items
            section.path = path
            section.loading = false
        },
        // Remonter au niveau `index` du fil d'Ariane (-1 = catégorie de tête). Cliquer le
        // niveau courant ne recharge rien : on y est déjà.
        goToSectionLevel(section, index) {
            if (index === section.path.length - 1) return

            this.loadSectionLevel(section, index < 0 ? [] : section.path.slice(0, index + 1))
        },
        // Le clic sur une catégorie fait défiler jusqu'à sa section : toutes sont déjà rendues,
        // on peut donc aussi les parcourir au défilement.
        goToRestoSection(key) {
            // Même exclusion que selectNode : parcourir le sommaire et chercher partout sont
            // deux intentions contraires. La grille repasse aux sections dès que le champ est vidé.
            if (this.searchQuery) {
                this.$emit('update:searchQuery', '')
            }

            this.activeRestoKey = key

            const target = this.$refs['resto-' + key]
            const element = Array.isArray(target) ? target[0] : target
            if (element && element.scrollIntoView) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        },
        /**
         * Identifiant stable d'une carte du menu.
         *
         * L'endpoint des packs ne renvoie PAS de champ `id` — seulement `uuid`. En construisant
         * la clé sur `food.id`, tous les packs partageaient donc la même valeur
         * (« menus-undefined ») : en sélectionner un les surlignait tous, et Vue voyait des
         * clés dupliquées. On prend l'uuid en priorité, présent des deux côtés.
         */
        foodKey(section, food) {
            return section.key + '-' + (food.uuid || food.id)
        },
        /**
         * Un plat qui demande une configuration : suppléments, options, ou menu (dont la
         * composition n'est visible que dans le panneau). Même prédicat qu'avant, il décide
         * simplement d'un dépliage plutôt que d'une popup.
         */
        foodNeedsConfig(food) {
            return !!(food && (food.type === 'pack' || food.has_addons || food.has_options))
        },
        isFoodConfigOpen(section, food) {
            return this.openFoodConfigKey === this.foodKey(section, food)
        },
        toggleFoodConfig(section, food) {
            const key = this.foodKey(section, food)
            this.openFoodConfigKey = this.openFoodConfigKey === key ? null : key
        },
        closeFoodConfig() {
            this.openFoodConfigKey = null
        },
        /**
         * Total de la ligne resto, suppléments compris : `entryTotal` sait déjà additionner les
         * exemplaires configurés et les exemplaires nus. On ne recalcule rien ici.
         */
        foodTotal(food) {
            const entry = this.restoBasket[this.basketKey(food)]

            return entry ? this.entryTotal(entry) : 0
        },
        /**
         * Retrait demandé depuis le RÉCAPITULATIF, l'entrée étant désignée par sa clé.
         *
         * Le panneau d'aperçu ne connaît pas l'article d'origine — il n'a que la ligne du
         * panier. `removeFood` et `removeProduct`, eux, partent de l'article : d'où ce point
         * d'entrée, qui évite au stepper de tenir une copie du panier pour retrouver l'objet.
         */
        removeSummaryEntry(type, sourceId) {
            if (type === 'resto') {
                if (!sourceId || !this.restoBasket[sourceId]) return

                this.$delete(this.restoBasket, sourceId)
                this.emitBasket()

                return
            }

            if (!sourceId || !this.productBasket[sourceId]) return

            this.$delete(this.productBasket, sourceId)
            this.emitProductBasket()
        },
        // Retrait complet de la ligne, toutes configurations confondues — le « - » ne retire
        // qu'une unité à la fois.
        removeFood(food) {
            const key = this.basketKey(food)
            if (!key || !this.restoBasket[key]) return

            this.$delete(this.restoBasket, key)
            this.emitBasket()
        },
        // Sélection purement visuelle pour l'instant : l'étape suivante n'est pas encore
        // ouverte au resto, rien n'est donc remonté au stepper.
        /**
         * Clic sur une carte du menu.
         *
         * Sans supplément ni option, le plat est ajouté DIRECTEMENT — rien à configurer, une
         * popup ne demanderait qu'à être refermée. Dès qu'il y a un supplément ou une option,
         * on passe par la popup de détail : le prix et la ligne dépendent de ces choix.
         *
         * Un menu (pack) passe TOUJOURS par la popup : sa composition — les plats de chaque
         * section de la formule — n'est visible que là.
         *
         * Une SOUS-CATÉGORIE n'est pas commandable : elle ouvre son contenu dans la section.
         */
        selectRestoItem(section, food) {
            if (this.isCategoryCard(food)) {
                this.openSectionCategory(section, food)
                return
            }

            if (this.foodNeedsConfig(food)) {
                this.toggleFoodConfig(section, food)
                return
            }

            this.addToBasket(food, 1)
        },
        // Clé de panier : l'uuid est le seul identifiant présent des deux côtés (les packs
        // n'exposent pas d'`id`).
        basketKey(food) {
            return food?.uuid || null
        },
        basketQty(food) {
            return this.restoBasket[this.basketKey(food)]?.quantity || 0
        },
        addToBasket(food, quantity, line = null) {
            const key = this.basketKey(food)
            if (!key || quantity <= 0) return

            const current = this.restoBasket[key]
            const entry = current
                ? { ...current, quantity: current.quantity + quantity }
                : {
                      uuid: key,
                      name: food.name,
                      price: Number(food.price) || 0,
                      // Taux catalogue : une ligne resto le recopie tel quel à l'écriture,
                      // l'aperçu du devis peut donc l'afficher sans rien dériver.
                      vat_rate: Number(food.vat_rate) || 0,
                      image: food.image || null,
                      // Slug de la catégorie du plat : le rail resto navigue par slug. On prend
                      // celui porté par le plat lui-même, seul fiable quand on a descendu dans
                      // une sous-catégorie.
                      category_slug: food.category?.slug || null,
                      // Plat ou menu : la charge utile envoyée au serveur n'est pas la même
                      // (`foods[]` contre `packs[]`). L'information vient de la carte cliquée
                      // ou de la popup, jamais d'une déduction sur le contenu du détail.
                      is_pack: !!(line ? line.is_pack : food.type === 'pack'),
                      quantity,
                      lines: [],
                  }

            // Un plat configuré garde le détail de CHAQUE configuration : deux burgers aux
            // suppléments différents ne peuvent pas être fondus en une seule ligne.
            if (line) {
                entry.lines = [...entry.lines, line]
            }

            this.$set(this.restoBasket, key, entry)
            this.emitBasket()
        },
        /**
         * Retrait d'une unité. Sur un plat configuré, c'est la DERNIÈRE configuration ajoutée
         * qui part — la carte ne montre qu'un total, elle ne permet pas de désigner laquelle,
         * et retirer la plus récente est le seul choix qui ne surprend pas.
         */
        decrementBasket(food) {
            const key = this.basketKey(food)
            const entry = this.restoBasket[key]
            if (!entry) return

            if (entry.quantity <= 1) {
                this.$delete(this.restoBasket, key)
                this.emitBasket()
                return
            }

            // Plat configuré : une seule ligne, dont la quantité suit celle de l'entrée. La
            // retirer ferait retomber le plat en ligne nue et perdrait ses suppléments.
            const config = this.foodConfigs[key]
            if (config && config.ready) {
                this.setConfiguredFood(food, entry.quantity - 1)
                return
            }

            const lines = entry.lines.slice(0, -1)
            this.$set(this.restoBasket, key, { ...entry, quantity: entry.quantity - 1, lines })
            this.emitBasket()
        },
        // Validation de la popup de détail : le plat configuré rejoint le panier.
        /**
         * Chaque changement de configuration s'applique immédiatement : il n'y a plus de bouton
         * à valider. Tant que la configuration est incomplète (`ready` faux), rien n'entre au
         * panier — c'est exactement ce que refusait le bouton désactivé d'avant.
         *
         * La configuration est mémorisée même quand elle n'est pas encore complète, pour que
         * refermer puis rouvrir le panneau retrouve les choix déjà faits.
         */
        handleFoodConfigChange(payload) {
            const key = this.basketKey(payload.food)
            if (!key) return

            this.$set(this.foodConfigs, key, {
                selections: payload.selections,
                packChoice: payload.packChoice,
                options: payload.options,
                count: payload.count,
                unit_price: payload.unit_price,
                base_price: payload.base_price,
                is_pack: payload.is_pack,
                ready: payload.ready,
            })

            if (!payload.ready) return

            // Le plat garde la quantité déjà au panier : reconfigurer 3 burgers ne les ramène
            // pas à 1. Sinon il y entre pour une unité.
            const current = this.restoBasket[key]
            this.setConfiguredFood(payload.food, current ? current.quantity : 1)
        },
        foodConfig(food) {
            return this.foodConfigs[this.basketKey(food)] || null
        },
        /**
         * Libellé de la pill : l'invitation à configurer tant que rien n'est choisi, puis le
         * RÉSUMÉ des choix — leur nombre et ce qu'ils ajoutent au prix de base.
         */
        foodConfigLabel(food) {
            const config = this.foodConfig(food)

            if (!config || !config.count) {
                return this.$t('devis.v3.food_options_to_configure')
            }

            const label = this.$tc('devis.v3.food_choices_count', config.count, { count: config.count })
            const supplement = (Number(config.unit_price) || 0) - (Number(config.base_price) || 0)

            return supplement > 0 ? label + ' · +' + this.formatPrice(supplement) : label
        },
        /**
         * Écrit la ligne d'un plat CONFIGURÉ : une seule ligne, dont la quantité suit celle de
         * l'entrée. Un plat configuré n'a qu'une configuration à la fois — c'est le principe du
         * panneau déplié — là où l'ancienne popup empilait une ligne par validation.
         *
         * `buildFoodQuotePayload` s'en accommode sans changement : une ligne unique couvrant
         * toute la quantité ne laisse aucun reliquat à envoyer en ligne nue.
         */
        setConfiguredFood(food, quantity) {
            const key = this.basketKey(food)
            const config = this.foodConfigs[key]
            if (!key || !config || quantity <= 0) return

            const unitPrice = Number(config.unit_price) || 0

            this.$set(this.restoBasket, key, {
                uuid: key,
                name: food.name,
                price: Number(food.price) || 0,
                vat_rate: Number(food.vat_rate) || 0,
                image: food.image || null,
                is_pack: !!config.is_pack,
                quantity,
                lines: [
                    {
                        quantity,
                        is_pack: !!config.is_pack,
                        options: config.options,
                        unit_price: unitPrice,
                        total: unitPrice * quantity,
                    },
                ],
            })
            this.emitBasket()
        },
        /**
         * Le « + » d'une ligne : une unité de PLUS de la même chose. Un plat configuré garde sa
         * configuration — rouvrir le panneau pour chaque exemplaire serait absurde ; un plat
         * simple emprunte le chemin habituel.
         */
        incrementFood(food) {
            const config = this.foodConfig(food)

            if (config && config.ready) {
                this.setConfiguredFood(food, this.basketQty(food) + 1)
                return
            }

            this.addToBasket(food, 1)
        },
        /**
         * Remonte le panier resto au stepper, accompagné de la charge utile prête pour
         * l'API. La mise en forme vit dans `foodQuotePayload` : le composant n'affiche,
         * il ne traduit pas.
         */
        emitBasket() {
            // Chaque entrée porte SON total, calculé par `entryTotal` — le seul endroit qui
            // sait ce que vaut un plat configuré. Le récapitulatif du footer l'affiche tel
            // quel plutôt que de refaire l'opération de son côté.
            const entries = Object.values(this.restoBasket).map((entry) => ({
                ...entry,
                total: this.entryTotal(entry),
            }))

            this.$emit('resto-change', {
                entries,
                count: entries.reduce((sum, entry) => sum + (entry.quantity || 0), 0),
                total: entries.reduce((sum, entry) => sum + entry.total, 0),
                payload: buildFoodQuotePayload(entries),
            })
        },
        /**
         * Total AFFICHÉ d'une entrée du panier — indicatif uniquement. Le montant qui
         * fera foi est celui que le serveur recalcule à l'enregistrement, à partir des
         * tarifs en base.
         */
        entryTotal(entry) {
            const configured = (entry.lines || []).reduce((sum, line) => sum + (Number(line.total) || 0), 0)
            const configuredQty = (entry.lines || []).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0)
            const plainQty = Math.max(0, (entry.quantity || 0) - configuredQty)

            return configured + plainQty * (Number(entry.price) || 0)
        },
        formatFoodPrice(food) {
            return this.formatPrice(food.price)
        },
        /**
         * Applique la recherche courante à l'onglet actif.
         *
         * Une recherche active DÉSÉLECTIONNE le nœud : on cherche « dans toutes les catégories »,
         * garder un nœud surligné laisserait croire à un filtre qui ne s'applique plus.
         * Le rail lui-même — libellés et compteurs — ne bouge pas.
         */
        applySearch() {
            if ((this.searchQuery || '').trim() !== '') {
                this.activeNode = null
            }

            if (this.isResto) {
                this.applyRestoSearch()
                return
            }

            this.resetAndLoad()
        },
        /**
         * Recherche resto : le catalogue de plats n'a pas d'endpoint devis dédié, on interroge
         * la même API que le sommaire (/api/v3/food/) sans borner les catégories.
         *
         * Les résultats sont rangés dans `restoSearchItems`, PAS dans `restoSections` : le rail
         * lit les sections, il resterait donc identique — c'est voulu.
         */
        async applyRestoSearch() {
            const term = (this.searchQuery || '').trim()

            if (term === '') {
                this.restoSearchItems = []
                return
            }

            this.restoLoading = true

            try {
                const { data } = await axios.get('/api/v3/food/', {
                    params: { name: term, page: 1, pagination: 200, all: true, table: null },
                })

                // Les cartes « catégorie » ouvrent un niveau, elles ne sont pas des plats :
                // hors sujet dans une liste de résultats à plat.
                this.restoSearchItems = (data?.data || []).filter((item) => item.type !== 'category')
            } catch (error) {
                console.error('Recherche resto impossible :', error)
                this.restoSearchItems = []
            } finally {
                this.restoLoading = false
                this.$nextTick(() => this.updateScrollAreaHeight())
            }
        },
        async resetAndLoad() {
            this.items = []
            this.page = 1
            this.hasMore = true
            await this.loadItems()
        },
        async loadItems() {
            if (this.loading || this.loadingMore || !this.hasMore) return

            if (this.page === 1) this.loading = true
            else this.loadingMore = true

            try {
                const params = {
                    type: this.activeType,
                    search: this.searchQuery,
                    page: this.page,
                    limit: 12,
                }
                // La recherche porte sur le NOM, dans TOUTES les catégories : tant qu'elle est
                // active, le filtre de catégorie n'est pas envoyé. Les deux s'excluent — cliquer
                // un nœud efface la recherche, chercher désélectionne le nœud (cf. selectNode /
                // debounceSearch) — pour qu'aucun écran ne montre un filtre qui ne s'applique pas.
                //
                // Le nœud dit lui-même sur QUOI filtrer (`param`/`value`) : tag, catégorie ou
                // sous-catégorie boutique. Résolu serveur, comme avant : la pagination interdit
                // tout filtrage client fiable.
                if (!params.search && this.activeNode) {
                    params[this.activeNode.param] = this.activeNode.value
                }

                const response = await axios.get('/back-office/v3/devis/items', { params })

                const paginatedData = response.data
                const newData = paginatedData.data || []

                if (this.page === 1) {
                    this.items = newData
                } else {
                    this.items = [...this.items, ...newData]
                }

                this.hasMore = paginatedData.current_page < paginatedData.last_page
                this.page++
            } catch (error) {
                console.error('Error loading items:', error)
            } finally {
                this.loading = false
                this.loadingMore = false
            }
        },
        handleScroll(e) {
            const { scrollTop, scrollHeight, clientHeight } = e.target
            if (scrollHeight - scrollTop <= clientHeight + 50) {
                this.loadItems()
            }
        },
        /**
         * Charge les chips de l'onglet courant : tags pour les expériences, sous-catégories
         * boutique pour les produits. Les autres onglets n'en ont pas.
         */
        async fetchTree() {
            this.tree = []
            this.treeLoading = true

            try {
                const { data } = await axios.get('/back-office/v3/devis/items/tree', {
                    params: { type: this.activeType },
                })
                this.tree = Array.isArray(data) ? data : []
            } catch (error) {
                console.error('Error loading tree:', error)
                this.tree = []
            } finally {
                this.treeLoading = false
            }
        },
        /**
         * Compteurs resto, par slug de catégorie. Chargés à part du sommaire : celui-ci vient de
         * l'API resto (qui ignore les compteurs), l'arbre du devis ne sert qu'à le décorer.
         */
        async fetchRestoCounts() {
            try {
                const { data } = await axios.get('/back-office/v3/devis/items/tree', { params: { type: 'resto' } })
                const counts = {}

                const walk = (nodes, depth) => {
                    if (depth >= 10) return
                    nodes.forEach((node) => {
                        counts[node.value] = node.count
                        if (node.children && node.children.length > 0) walk(node.children, depth + 1)
                    })
                }

                walk(Array.isArray(data) ? data : [], 0)
                this.restoCounts = counts
            } catch (error) {
                // Un compteur absent n'empêche pas de composer un devis : le rail s'affiche sans.
                console.error('Error loading resto counts:', error)
                this.restoCounts = {}
            }
        },
        /**
         * Compteur d'un slug, ou `null` s'il n'est pas connu — un compteur inventé (0 par
         * défaut) ferait croire qu'une catégorie est vide alors qu'elle n'a pas été comptée.
         */
        slugCount(slug) {
            const value = this.restoCounts[slug]

            return value === undefined ? null : value
        },
        // « Menus » n'est pas une catégorie de plats : il n'a pas de compteur dans l'arbre.
        restoCount(section) {
            return section.slug ? this.slugCount(section.slug) : null
        },
        // Clé d'un nœud : le couple param/value, jamais l'id seul — un tag #3, une catégorie
        // boutique #3 et une sous-catégorie #3 coexistent sans rapport entre elles.
        nodeKey(node) {
            return node.param + ':' + node.value
        },
        /**
         * Lignes composées sous ce nœud, SA DESCENDANCE COMPRISE.
         *
         * Le report se fait ici et non à la source : un article ne porte qu'un tag, le compter
         * une fois par ancêtre en amont le ferait apparaître plusieurs fois dans le total du
         * type. Même principe que les compteurs d'articles, mais remontés côté client — ils
         * changent à chaque composition, un aller-retour serveur n'aurait pas de sens.
         */
        composedForNode(node) {
            let total = this.composedByNode[this.nodeKey(node)] || 0

            ;(node.children || []).forEach((child) => {
                total += this.composedForNode(child)
            })

            return total
        },
        // Plats composés sous une catégorie resto. Le rail navigue par slug, la clé aussi.
        composedForSlug(slug) {
            return slug ? this.composedByNode['resto:' + slug] || 0 : 0
        },
        /**
         * Plats composés sous une section, ses sous-catégories comprises.
         *
         * Le sommaire resto n'est pas l'arbre aplati : il a son propre balisage, et ses
         * sous-catégories sont figées dans `section.subcategories`. On somme donc ici plutôt
         * que de réutiliser composedForNode, qui parcourt des nœuds d'une autre forme.
         */
        composedRestoSection(section) {
            let total = this.composedForSlug(section.slug)

            ;(section.subcategories || []).forEach((category) => {
                total += this.composedForSlug(category.slug)
            })

            return total
        },
        composedCountFor(typeId) {
            return this.composedByType[typeId] || 0
        },
        isNodeOpen(key) {
            return this.expandedNodes.includes(key)
        },
        toggleNode(key) {
            this.expandedNodes = this.isNodeOpen(key) ? this.expandedNodes.filter((k) => k !== key) : [...this.expandedNodes, key]
        },
        /**
         * Sélectionne un nœud du rail (ou `null` pour « Tous ») et recharge la grille.
         *
         * Efface la recherche : filtrer par catégorie et chercher dans toutes les catégories
         * sont deux intentions contraires ; les laisser coexister afficherait un nœud surligné
         * pendant que la grille montre autre chose.
         */
        selectNode(node) {
            const key = node ? this.nodeKey(node) : null
            if (this.activeNodeKey === key && !this.searchQuery) return

            this.activeNode = node

            if (this.searchQuery) {
                this.$emit('update:searchQuery', '')
                // La remise à zéro déclenche déjà un rechargement via le watcher : on s'arrête
                // là pour ne pas lancer deux fois la même requête.
                return
            }

            this.resetAndLoad()
        },
        // Une carte produit alimente un panier ; une expérience ou un package reste une
        // sélection unique qui ouvre les étapes suivantes du stepper.
        onCardClick(item) {
            if (this.isProduct) {
                this.addProductToBasket(item)
                return
            }

            this.selectItem(item)
        },
        /**
         * Prix unitaire affiché. Sur l'article retenu, c'est celui que le serveur vient de
         * calculer pour la formule et le nombre de participants ; ailleurs, le prix catalogue.
         */
        // Libellé affiché : celui saisi sur l'article retenu, sinon le nom du catalogue.
        rowLabel(item) {
            if (this.showsParticipants(item) && (this.itemLabel || '').trim() !== '') {
                return this.itemLabel
            }

            return item.name
        },
        // Note de l'article retenu. Les autres lignes n'en portent pas : une seule expérience
        // part au devis à la fois.
        rowNote(item) {
            return this.showsParticipants(item) ? (this.itemNote || '').trim() : ''
        },
        /**
         * Ouvrir un champ SÉLECTIONNE d'abord la ligne : la note et le libellé appartiennent à
         * l'article qu'on ajoute, pas à celui qu'on survole.
         */
        startLabelEdit(item) {
            if (!this.showsParticipants(item)) {
                this.selectItem(item)
                this.$emit('update:itemLabel', item.name)
            } else if ((this.itemLabel || '').trim() === '') {
                this.$emit('update:itemLabel', item.name)
            }

            this.noteEditingId = null
            this.labelEditingId = item.id
            this.$nextTick(() => {
                const input = Array.isArray(this.$refs.labelInput) ? this.$refs.labelInput[0] : this.$refs.labelInput
                input?.focus()
                input?.select()
            })
        },
        /**
         * Ouvre le choix de la date pour CETTE ligne. Comme les autres actions de ligne, elle
         * sélectionne d'abord l'article : la date appartient à ce qu'on ajoute.
         */
        openPlanning(item) {
            if (!this.showsParticipants(item)) this.selectItem(item)

            this.labelEditingId = null
            this.noteEditingId = null
            this.priceEditingId = null
            this.$emit('open-planning')
        },
        startNoteEdit(item) {
            if (!this.showsParticipants(item)) this.selectItem(item)

            this.labelEditingId = null
            this.noteEditingId = item.id
        },
        rowUnitPrice(item) {
            // Un prix négocié l'emporte sur le tarif calculé : c'est une décision commerciale.
            if (this.isNegotiated(item)) {
                return this.itemPrice
            }

            if (this.showsParticipants(item) && this.livePrice) {
                return this.livePrice.unit_price
            }

            return item.price
        },
        /**
         * Ligne d'un BILLET GROUPÉ (simpleTicket) : sa ligne principale est un conteneur à 0 €,
         * tout le montant étant porté par ses billets. Le prix ne s'y négocie donc pas — le
         * serveur le refuse déjà (DevisStoreService::applyEditorialFields), on évite ici
         * l'aller-retour saisie → erreur.
         *
         * Connu seulement pour l'article retenu : `subcategory_slug` vient de ses détails, qui
         * ne sont chargés qu'à la sélection. Sur une ligne non retenue le crayon reste affiché ;
         * cliquer dessus sélectionne l'article, et le crayon disparaît dès l'arrivée des détails.
         */
        isTicketContainerRow(item) {
            return this.showsParticipants(item) && this.itemDetails?.subcategory_slug === 'simpleTicket'
        },
        isNegotiated(item) {
            return this.showsParticipants(item) && Number.isFinite(this.itemPrice)
        },
        /**
         * Total de la ligne. La QUANTITÉ vient du serveur (un billet par participant, ou une
         * seule ligne si l'espace est privatisé) : on ne la redevine pas, on la reprend — même
         * quand le prix unitaire a été négocié.
         */
        rowLineTotal(item) {
            if (!this.showsParticipants(item) || !this.livePrice) return null

            return this.rowUnitPrice(item) * (this.livePrice.quantity || 1)
        },
        startPriceEdit(item) {
            if (!this.showsParticipants(item)) this.selectItem(item)

            this.labelEditingId = null
            this.noteEditingId = null
            this.priceEditingId = item.id
            this.$nextTick(() => {
                const input = Array.isArray(this.$refs.priceInput) ? this.$refs.priceInput[0] : this.$refs.priceInput
                input?.focus()
                input?.select()
            })
        },
        /**
         * Un champ vidé rend la main au tarif calculé (`null`) plutôt que de valoir zéro — sinon
         * effacer pour se raviser offrirait la prestation.
         */
        onPriceInput(event) {
            const raw = event.target.value

            if (raw === '' || raw === null) {
                this.$emit('update:itemPrice', null)
                return
            }

            const value = Number(raw)
            this.$emit('update:itemPrice', Number.isFinite(value) && value >= 0 ? value : null)
        },
        // Les réglages ne se posent que sur l'article retenu, et là où ils ont un sens : un
        // produit se met au panier, il n'a ni formule ni participants.
        showsParticipants(item) {
            return !this.isProduct && !this.isResto && this.selectedId === item.id
        },
        // Article déjà composé et mis de côté, en attente de la validation du lot.
        isStaged(item) {
            return this.stagedIds.includes(Number(item.id))
        },
        isCardSelected(item) {
            return this.isProduct ? this.productQty(item) > 0 : this.selectedId === item.id
        },
        productQty(item) {
            return this.productBasket[item?.id]?.quantity || 0
        },
        addProductToBasket(item, quantity = 1) {
            if (!item || !item.id || quantity <= 0) return

            const current = this.productBasket[item.id]
            const entry = current
                ? { ...current, quantity: current.quantity + quantity }
                : {
                      id: item.id,
                      name: item.name,
                      price: Number(item.price) || 0,
                      vat_rate: Number(item.vat_rate) || 0,
                      image: item.image || null,
                      // Rattachement retenu à l'ajout : le nœud du menu est désigné par un
                      // identifiant, et l'entrée du panier ne garde pas l'article d'origine.
                      subcategory_id: item.subcategory_id || null,
                      category_id: item.category_id || null,
                      quantity,
                  }

            this.$set(this.productBasket, item.id, entry)
            this.emitProductBasket()
        },
        // Retrait complet de la ligne produit ; le « - » ne retire qu'une unité.
        removeProduct(item) {
            if (!item || !this.productBasket[item.id]) return

            this.$delete(this.productBasket, item.id)
            this.emitProductBasket()
        },
        decrementProduct(item) {
            const entry = this.productBasket[item?.id]
            if (!entry) return

            if (entry.quantity <= 1) {
                this.$delete(this.productBasket, item.id)
            } else {
                this.$set(this.productBasket, item.id, { ...entry, quantity: entry.quantity - 1 })
            }

            this.emitProductBasket()
        },
        /**
         * Remonte le panier produits au stepper avec sa charge utile.
         *
         * ⚠️ Frontend = zéro confiance : on n'envoie QUE des identifiants et des quantités.
         * Le total transmis n'est qu'un indicatif d'affichage — le serveur relit chaque
         * tarif en base au moment d'écrire la ligne.
         */
        emitProductBasket() {
            // Même principe que le panier resto : le total de chaque entrée accompagne
            // l'entrée, et le total du panier se contente de les additionner.
            const entries = Object.values(this.productBasket).map((entry) => ({
                ...entry,
                total: (entry.quantity || 0) * (Number(entry.price) || 0),
            }))

            this.$emit('product-change', {
                entries,
                count: entries.reduce((sum, entry) => sum + (entry.quantity || 0), 0),
                total: entries.reduce((sum, entry) => sum + entry.total, 0),
                payload: {
                    products: entries.map((entry) => ({ id: entry.id, quantity: entry.quantity })),
                },
            })
        },
        formatPrice(value) {
            const price = Number(value)
            if (!Number.isFinite(price)) return ''

            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price)
        },
        selectItem(item) {
            this.$emit('select', item)
        },
    },
}
</script>

<style scoped>
/* RESTO — sommaire empilé : un titre par section, puis la même grille de cartes que les
   expériences. scroll-margin-top évite que le titre passe sous le bord haut de la zone
   défilante quand on saute à une section depuis les chips. */
.resto-section {
    scroll-margin-top: 8px;
}

.resto-section + .resto-section {
    margin-top: 24px;
}

.resto-section-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0 0 12px;
}

/* Fil d'Ariane de section : les niveaux traversés restent cliquables, le niveau courant est
   affiché comme le titre qu'il remplace (il ne mène nulle part). */
.resto-crumb {
    color: #7d2dbd;
    transition: opacity 0.2s ease;
}

.resto-crumb:hover {
    opacity: 0.7;
}

.resto-crumb.is-current {
    color: #1d1d1b;
    cursor: default;
}

.resto-crumb.is-current:hover {
    opacity: 1;
}

.resto-crumb-sep {
    color: #94a3b8;
}

.resto-section-empty {
    margin: 0;
}

.step-experience-selection {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.scroll-area-v3 {
    overflow-y: scroll;
    padding: 24px;
}

/* ------------------------------------------------------------------ *
 *  LISTE d'articles — une ligne par article, en remplacement des cartes
 * ------------------------------------------------------------------ */

.item-list-v3 {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-bottom: 20px;
}

/* Grille explicite plutôt qu'un flex : les colonnes prix / quantité / total s'alignent d'une
   ligne à l'autre, ce qu'un flex ne garantit pas quand les libellés n'ont pas la même longueur.
   `minmax(0, 1fr)` sur la colonne du nom permet à l'ellipse de fonctionner. */
.item-row-v3 {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) auto auto auto auto;
    align-items: center;
    gap: 14px;
    padding: 10px 14px;
    border: 1px solid #e9edf3;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease,
        background 0.18s ease;
}

.item-row-v3:hover {
    border-color: #cbb6df;
    box-shadow: 0 2px 10px rgba(29, 29, 27, 0.06);
}

.item-row-v3.selected {
    border-color: #7d2dbd;
    background: rgba(125, 45, 189, 0.04);
}

/* Le panneau de configuration se colle sous sa ligne : on efface l'arrondi du bas pour que
   les deux se lisent comme un seul bloc. */
.item-row-v3.is-open {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-color: transparent;
}

.row-thumb {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    overflow: hidden;
    background: #f1f5f9;
    flex: 0 0 auto;
}

.row-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.row-main {
    min-width: 0;
}

.row-title {
    margin: 0;
    color: #1d1d1b;
    font-size: 14.5px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Actions révélées au SURVOL de la ligne : renommer, annoter. Discrètes au repos pour ne pas
   charger une liste dense, mais toujours présentes dans le DOM — les faire apparaître au survol
   ne doit pas décaler le contenu. */
.row-inline-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-left: 6px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 0.18s ease,
        background 0.18s ease,
        color 0.18s ease;
}

.item-row-v3:hover .row-inline-action,
.row-inline-action:focus-visible {
    opacity: 1;
}

.row-inline-action:hover {
    background: rgba(125, 45, 189, 0.1);
    color: #7d2dbd;
}

.row-title-edit {
    display: flex;
    align-items: center;
}

.row-title-input {
    width: 100%;
    padding: 2px 8px;
    border: 1px solid #7d2dbd;
    border-radius: 8px;
    outline: none;
    background: #fff;
    color: #1d1d1b;
    font-size: 14.5px;
}

.row-note-edit {
    margin-top: 3px;
}

/* Le bouton de note reste effacé tant que la ligne n'est pas survolée ET qu'aucune note n'est
   saisie : une note existante doit rester visible en permanence, c'est une information. */
.row-note-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 1px dashed #dfe4ec;
    border-radius: 9999px;
    background: transparent;
    color: #94a3b8;
    font-family: 'Poppins', sans-serif;
    font-size: 11.5px;
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 0.18s ease,
        border-color 0.18s ease,
        color 0.18s ease;
}

.item-row-v3:hover .row-note-btn,
.row-note-btn:focus-visible,
.row-note-btn.has-note {
    opacity: 1;
}

.row-note-btn.has-note {
    border-style: solid;
    border-color: rgba(125, 45, 189, 0.3);
    color: #7d2dbd;
}

.row-note-btn:hover {
    border-color: #7d2dbd;
    color: #7d2dbd;
}

.row-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 3px;
}

/* Pill discrète : elle qualifie la ligne (durée, rattachement, configuration à faire) sans
   entrer en concurrence avec le nom, qui reste l'élément principal. */
.row-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 9999px;
    background: #f1f5f9;
    color: #64748b;
    font-family: 'Poppins', sans-serif;
    font-size: 11.5px;
    line-height: 1.5;
    white-space: nowrap;
}

.row-pill--config {
    background: rgba(125, 45, 189, 0.1);
    color: #7d2dbd;
}

.row-note {
    color: #7d2dbd;
    font-family: 'Poppins', sans-serif;
    font-size: 11.5px;
    white-space: nowrap;
}

.row-price-edit {
    display: flex;
    justify-content: flex-end;
}

.row-price-input {
    width: 92px;
    padding: 2px 8px;
    border: 1px solid #7d2dbd;
    border-radius: 8px;
    outline: none;
    background: #fff;
    color: #1d1d1b;
    font-size: 13.5px;
    text-align: right;
    font-variant-numeric: tabular-nums;
}

/* Un prix négocié se distingue du tarif calculé : sans ce repère, rien ne dirait que le montant
   ne vient plus du catalogue. */
.row-price.negotiated {
    color: #7d2dbd;
}

.row-price {
    color: #475569;
    font-size: 13.5px;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

/* Sélecteur de quantité. Le placeholder garde la colonne à la même largeur sur les lignes
   sans quantité : sans lui, prix et total sautaient d'une ligne à l'autre. */
.row-qty,
.row-qty-placeholder {
    display: flex;
    align-items: center;
    gap: 2px;
    min-width: 96px;
    justify-content: center;
}

.row-qty button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    color: #475569;
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        color 0.18s ease,
        background 0.18s ease;
}

.row-qty button:hover:not(:disabled) {
    border-color: #7d2dbd;
    color: #7d2dbd;
    background: rgba(125, 45, 189, 0.06);
}

.row-qty button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.row-qty .qty-value {
    min-width: 26px;
    text-align: center;
    color: #1d1d1b;
    font-size: 13.5px;
    font-variant-numeric: tabular-nums;
}

.row-total {
    min-width: 78px;
    color: #1d1d1b;
    font-size: 14px;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.row-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.row-remove:hover {
    background: rgba(182, 43, 34, 0.1);
    color: #b62b22;
}

.row-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #7d2dbd;
    color: #fff;
}

.row-chevron {
    color: #94a3b8;
}

/* Une sous-catégorie ouvre un niveau, elle ne se commande pas : ni prix, ni quantité. */
.item-row-v3.is-category {
    grid-template-columns: 44px minmax(0, 1fr) auto;
}

.item-row-v3.is-category:hover {
    border-color: #7d2dbd;
}

/* Panneau de configuration déplié sous la ligne. */
/* Les réglages remplacent les pills sous le nom : ils occupent la largeur de la colonne du
   milieu, la ligne ne change donc pas de structure. */
.row-participants {
    margin-top: 2px;
}

.row-config-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px 0;
}

.row-config {
    margin-top: -6px;
    padding: 16px;
    border: 1px solid #7d2dbd;
    border-top: 0;
    border-radius: 0 0 12px 12px;
    background: #fff;
    cursor: default;
}

.skeleton-row-v3 {
    height: 66px;
    border-radius: 12px;
    background: linear-gradient(90deg, #f1f5f9 25%, #e8edf4 37%, #f1f5f9 63%);
    background-size: 400% 100%;
    animation: skeleton-shimmer 1.4s ease infinite;
}

@keyframes skeleton-shimmer {
    0% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0 50%;
    }
}

/* Sous 1100px le prix unitaire s'efface : le total et la quantité portent l'essentiel. */
@media (max-width: 1100px) {
    .item-row-v3 {
        grid-template-columns: 40px minmax(0, 1fr) auto auto auto;
        gap: 10px;
    }

    .row-price {
        display: none;
    }
}

/* ------------------------------------------------------------------ *
 *  Catalogue : menu vertical à gauche + grille à droite
 * ------------------------------------------------------------------ */

.catalog-layout {
    display: flex;
    align-items: stretch;
    min-height: 0;
}

.catalog-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.catalog-nav {
    flex: 0 0 236px;
    width: 236px;
    overflow-y: auto;
    padding: 20px 10px 20px 20px;
    border-right: 1px solid #e9edf3;
    scrollbar-width: thin;
}

@media (max-width: 1279px) {
    .catalog-nav {
        flex-basis: 188px;
        width: 188px;
    }
}

/* Recherche en tête du rail : chercher et parcourir sont le même geste.
   Le champ est TOUJOURS ouvert — pas de loupe à déplier — et occupe toute la largeur de la
   colonne : c'est la première chose sur laquelle tombe le regard en ouvrant le popup. */
.nav-search {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 8px 10px;
    border: 1px solid #dfe4ec;
    border-radius: 10px;
    background: #fff;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.nav-search:focus-within {
    border-color: #7d2dbd;
    box-shadow: 0 0 0 3px rgba(125, 45, 189, 0.12);
}

.nav-search-icon {
    flex: 0 0 auto;
    color: #94a3b8;
}

.nav-search:focus-within .nav-search-icon {
    color: #7d2dbd;
}

.nav-search-input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #1d1d1b;
    font-size: 13.5px;
    line-height: 1.4;
}

.nav-search-input::placeholder {
    color: #94a3b8;
}

.nav-search-clear {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.nav-search-clear:hover {
    background: rgba(125, 45, 189, 0.1);
    color: #7d2dbd;
}

.nav-tree {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

/* Compteur d'articles du nœud, descendants inclus. Chiffres tabulaires pour que la colonne
   reste alignée d'une ligne à l'autre, et `flex-shrink: 0` pour qu'il ne soit jamais rogné
   au profit d'un libellé long — c'est le libellé qui s'ellipse. */
.nav-count {
    flex: 0 0 auto;
    min-width: 22px;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #eef1f6;
    color: #64748b;
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    line-height: 1.5;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.nav-child-row:hover .nav-count {
    background: rgba(125, 45, 189, 0.12);
    color: #7d2dbd;
}

.nav-group + .nav-group {
    margin-top: 6px;
}

/* Niveau 1 — le type (Expérience / Options / Resto) */
.nav-type {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 9px 10px;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #1d1d1b;
    font-size: 15px;
    text-align: left;
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.nav-type:hover {
    background: rgba(125, 45, 189, 0.08);
}

.nav-type.active {
    background: rgba(125, 45, 189, 0.12);
    color: #7d2dbd;
}

/* Le chevron pivote au lieu d'être remplacé : une seule icône importée, et l'ouverture
   se lit comme une rotation plutôt que comme un changement de glyphe. */
.nav-caret {
    flex: 0 0 auto;
    transition: transform 0.18s ease;
}

.nav-caret.open {
    transform: rotate(90deg);
}

.nav-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Niveau 2 — les catégories. Le filet vertical matérialise le rattachement au type. */
.nav-children {
    list-style: none;
    margin: 2px 0 0 17px;
    padding-left: 13px;
    border-left: 1px solid #ece7f3;
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.nav-child-row {
    display: flex;
    align-items: center;
    gap: 2px;
}

.nav-child {
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #475569;
    font-size: 13.5px;
    font-family: 'Poppins', sans-serif;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.nav-child:hover {
    background: rgba(125, 45, 189, 0.06);
    color: #7d2dbd;
}

.nav-child.active {
    background: rgba(125, 45, 189, 0.12);
    color: #7d2dbd;
}

/* Le chevron d'une catégorie est un bouton DISTINCT de son libellé : cliquer le nom garde
   son action d'origine (filtre ou ancre), déplier n'est qu'un geste d'affichage. */
.nav-toggle {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.nav-toggle:hover {
    background: rgba(125, 45, 189, 0.08);
    color: #7d2dbd;
}

/* Niveau 3 — les sous-catégories */
.nav-grandchildren {
    list-style: none;
    margin: 1px 0 2px 11px;
    padding-left: 13px;
    border-left: 1px solid #f1eef6;
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.nav-grandchild {
    flex: 1;
    min-width: 0;
    padding: 5px 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #64748b;
    font-size: 13px;
    font-family: 'Poppins', sans-serif;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.nav-grandchild:hover {
    background: rgba(125, 45, 189, 0.06);
    color: #7d2dbd;
}

.nav-grandchild.active {
    color: #7d2dbd;
    font-weight: 600;
}

.nav-empty {
    padding: 6px 10px;
    color: #94a3b8;
    font-size: 13px;
}

@keyframes pulse {
    0% {
        opacity: 1;
    }

    50% {
        opacity: 0.5;
    }

    100% {
        opacity: 1;
    }
}

.empty-state-v3 {
    text-align: center;
    padding: 40px;
    color: #64748b;
}

.loading-more-container {
    display: flex;
    justify-content: center;
    padding: 20px;
}

.spinner-v3-small {
    width: 20px;
    height: 20px;
    border: 2px solid #f1f5f9;
    border-top-color: #7d2dbd;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
@media (min-width: 768.99px) and (max-width: 1470px) {
}
@media (max-width: 768px) {
}

/* Choix de la date sur la ligne. Même traitement que la note : discret au repos, mais toujours
   visible dès qu'une décision a été prise — la date est une information, pas une commande. */
.row-schedule-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border: 1px dashed #dfe4ec;
    border-radius: 9999px;
    background: transparent;
    color: #94a3b8;
    font-family: 'Poppins', sans-serif;
    font-size: 11.5px;
    white-space: nowrap;
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 0.18s ease,
        border-color 0.18s ease,
        color 0.18s ease;
}

.item-row-v3:hover .row-schedule-btn,
.item-row-v3.selected .row-schedule-btn,
.row-schedule-btn:focus-visible,
.row-schedule-btn.has-value {
    opacity: 1;
}

.row-schedule-btn.has-value {
    border-style: solid;
    border-color: rgba(125, 45, 189, 0.3);
    color: #7d2dbd;
}

.row-schedule-btn:hover {
    border-color: #7d2dbd;
    color: #7d2dbd;
}

/* Le ✓ de sélection et le × de retrait occupent la MÊME cellule : la grille conserve ses six
   colonnes, et l'apparition du × au survol ne décale donc rien. */
.row-check-cell {
    position: relative;
    width: 26px;
    height: 26px;
}

.row-check-cell .row-check,
.row-check-cell .row-remove--swap {
    position: absolute;
    inset: 0;
    transition: opacity 0.18s ease;
}

.row-remove--swap {
    opacity: 0;
}

.item-row-v3:hover .row-check-cell .row-check {
    opacity: 0;
}

.item-row-v3:hover .row-remove--swap,
.row-remove--swap:focus-visible {
    opacity: 1;
}

/* Repère « vous avez composé ici » sur un type du menu. Volontairement en violet plein, pour
   se distinguer du compteur d'articles des catégories, qui lui est gris et purement
   informatif : celui-ci porte une action en cours. */
.nav-badge {
    flex: 0 0 auto;
    min-width: 20px;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #7d2dbd;
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    line-height: 1.5;
    text-align: center;
    font-variant-numeric: tabular-nums;
}
</style>
